import { ref, reactive, onMounted } from 'vue';
import { loadSiteConfig, saveSiteConfig } from '../services/dbApi';

const config = reactive({
  site: {},
  navigation: {},
  colors: {},
  homepage: {},
  pageTexts: {},
  impressumPage: {},
  contactPage: {}
});

const loading = ref(false);
const error = ref(null);

// Load site config
export async function loadConfig() {
  loading.value = true;
  error.value = null;
  try {
    const data = await loadSiteConfig();
    Object.assign(config, data);
    return data;
  } catch (err) {
    error.value = err.message;
    console.error('Error loading config:', err);
  } finally {
    loading.value = false;
  }
}

// Persist site config
export async function saveConfig() {
  loading.value = true;
  error.value = null;
  try {
    return await saveSiteConfig(config);
  } catch (err) {
    error.value = err.message;
    console.error('Error saving config:', err);
    throw err;
  } finally {
    loading.value = false;
  }
}

// Map color config to CSS variables
export function applyColors() {
  const root = document.documentElement;
  const colors = config.colors || {};
  const themePrimaryColor = colors.themePrimaryColor || colors.primaryColor || '#2d1b14';
  const themeOnPrimaryTextColor = colors.themeOnPrimaryTextColor || colors.secondaryColor || '#f5e9d4';
  const interactiveAccentColor = colors.interactiveAccentColor || colors.accentColor || '#8b6f47';
  const bodyTextColor = colors.bodyTextColor || colors.textColor || '#333333';
  const pageBackgroundColor = colors.pageBackgroundColor || colors.backgroundColor || '#ffffff';
  const pageBackgroundImage = colors.pageBackgroundImage || '';
  const headerBackgroundColor = colors.headerBackgroundColor || colors.headerBgColor || '#f9f9f9';
  const footerBackgroundColor = colors.footerBackgroundColor || colors.footerBgColor || '#f9f9f9';
  const buttonBackgroundColor = colors.buttonBackgroundColor || colors.buttonBgColor || '#8b6f47';
  const buttonLabelColor = colors.buttonLabelColor || colors.buttonTextColor || '#ffffff';

  root.style.setProperty('--theme-primary-color', themePrimaryColor);
  root.style.setProperty('--theme-on-primary-text-color', themeOnPrimaryTextColor);
  root.style.setProperty('--interactive-accent-color', interactiveAccentColor);
  root.style.setProperty('--body-text-color', bodyTextColor);
  root.style.setProperty('--page-background-color', pageBackgroundColor);
  root.style.setProperty('--page-background-image', pageBackgroundImage ? `url("${pageBackgroundImage}")` : 'none');
  root.style.setProperty('--header-background-color', headerBackgroundColor);
  root.style.setProperty('--footer-background-color', footerBackgroundColor);
  root.style.setProperty('--button-background-color', buttonBackgroundColor);
  root.style.setProperty('--button-label-color', buttonLabelColor);

  // Clear global font overrides so rich-text fields control typography locally.
  root.style.removeProperty('--body-font-family');
  root.style.removeProperty('--heading-font-family');
  root.style.removeProperty('--nav-brand-font-family');
  root.style.removeProperty('--nav-link-font-family');
  root.style.removeProperty('--footer-font-family');
  root.style.removeProperty('--button-font-family');
  root.style.removeProperty('--body-font-style');
  root.style.removeProperty('--heading-font-style');
  root.style.removeProperty('--nav-brand-font-style');
  root.style.removeProperty('--nav-link-font-style');
  root.style.removeProperty('--footer-font-style');
  root.style.removeProperty('--button-font-style');
}

export function useConfig() {
  onMounted(() => {
    loadConfig().then(() => applyColors());
  });

  return {
    config,
    loading,
    error,
    loadConfig,
    saveConfig,
    applyColors
  };
}
